// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Citation.tsx
 * @input Uses React, StyleX, theme tokens
 * @output Exports Citation component for inline citation references
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Citation/index.ts (exports if types change)
 * - /packages/cli/templates/blocks/components/Citation/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typeScaleVars, fontWeightVars, borderVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  label: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    kXLuUW: "x11njtxf",
    kZKoxP: "x1grt7ep",
    kGuDYH: "x141an7d",
    k63SB2: "xv8on6e",
    kLWn49: "x1ltkj2j",
    kMwMTN: "xv1l7n4",
    kaIpWk: "xh6dtrn",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x14i3s5s",
    kg3NbH: "xf314gf",
    keTefX: "xladpz9",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x1eaenvl",
    kIyJzY: "x2rcmwa",
    kAMwcw: "xlr8y92",
    ks0D6T: "x55n99s",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  labelWithIcon: {
    kZCmMZ: "x138rykx",
    $$css: true
  },
  labelHover: {
    kWkggS: "xe9uy6x",
    kMwMTN: "xv1l7n4 x140uwzg",
    $$css: true
  },
  number: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kXLuUW: "x1v0h343",
    kGuDYH: "x141an7d",
    k63SB2: "x2mo6ok",
    kLWn49: "x1ltkj2j",
    kMwMTN: "xv1l7n4",
    kWkggS: "xgcxg3y",
    kaIpWk: "xjspbzw",
    k7Eaqz: "x16asifk",
    kZKoxP: "x1grt7ep",
    kg3NbH: "x7a5moj",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x15406qy",
    kIyJzY: "x2rcmwa",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  numberHover: {
    kWkggS: "xgcxg3y xe9uy6x",
    $$css: true
  }
};
export function Citation({
  ref,
  source,
  number,
  variant = 'label',
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...rest
}) {
  const t = useTranslator();
  const title = source.title ?? String(number);
  const href = source.url;
  const icon = source.icon;
  const Tag = href ? 'a' : 'span';
  // `doc-noteref` is a reference role — only appropriate on the interactive
  // link form. On a plain (unlinked) span it is not a permitted role
  // (axe: aria-allowed-role), so omit it there; the aria-label still names it.
  const noteRole = href ? 'doc-noteref' : undefined;
  const linkProps = href ? {
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
    title
  } : {
    title
  };

  // Both <a> and <span> extend HTMLElement — narrow via intersection
  const elementRef = ref;
  if (variant === 'number') {
    return /*#__PURE__*/_jsx(Tag, {
      ...rest,
      ref: elementRef,
      role: noteRole,
      "aria-label": t('@astryx.citation.label', {
        number,
        title
      }),
      "data-testid": testId,
      ...linkProps,
      ...mergeProps(themeProps('citation', {
        variant
      }), stylex.props(styles.number, href != null && styles.numberHover, xstyle), className, style),
      children: number
    });
  }
  return /*#__PURE__*/_jsxs(Tag, {
    ...rest,
    ref: elementRef,
    role: noteRole,
    "aria-label": t('@astryx.citation.label', {
      number,
      title
    }),
    "data-testid": testId,
    ...linkProps,
    ...mergeProps(themeProps('citation', {
      variant
    }), stylex.props(styles.label, icon != null && styles.labelWithIcon, href != null && styles.labelHover, xstyle), className, style),
    children: [icon && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x3nfvp2 x6s0dn4 xl56j7k x12xnipv x6b6gus xjspbzw x10xzikg x1litavf x1y0btm7 x14i3s5s xb3r6kr x2lah0s"
      },
      children: /*#__PURE__*/_jsx("img", {
        src: icon,
        alt: "",
        "aria-hidden": "true",
        ...{
          className: "x1ad1g3v x9gl9lo"
        }
      })
    }), /*#__PURE__*/_jsx("span", {
      ...{
        className: "xb3r6kr xlyipyv xuxw1ft xeuugli"
      },
      children: title
    })]
  });
}
Citation.displayName = 'Citation';